// client/src/api/services/plan.ts
import { apiClient } from '../client';
import { ENDPOINTS, buildQueryString } from '../endpoints';
import { getErrorMessage, getErrorCode } from '@/utils/errorUtils';
import { AxiosError } from "axios";

// ==================== TYPES ====================
export interface Plan {
  id: string;
  name: string;
  price: number;
  period: 'day' | 'week' | 'month';
  features: string[];
  dataLimit: number | null;
  dataLimitUnit: string;
  hasUnlimitedAccess: boolean;
  // T-04 fields
  price_minor?: number;
  currency?: string;
  country_code?: string;
  duration_days?: number;
  rate_down_kbps?: number | null;
  rate_up_kbps?: number | null;
  rate_after_fup_kbps?: number | null;
}

export interface PlansResponse {
  success: boolean;
  data: {
    daily: Plan[];
    weekly: Plan[];
    monthly: Plan[];
  };
}

export interface SubscribeRequest {
  paymentMethod: 'wallet' | 'card' | 'crypto';
  hotspotId?: string;
  autoRenew?: boolean;
  planId?: string;
  walletType?: 'celo' | 'kxt' | 'fiat';
  balanceType?: 'celo' | 'kxt' | 'fiat';
  walletSource?: 'celo' | 'kxt' | 'fiat';
  crypto?: 'USDT' | 'USDC';
  chain?: 'SOLANA_DEVNET'  | 'BSC_TESTNET';
  baseFiat?: string;
}

export interface SubscribeResponse {
  success: boolean;
  message?: string;
  data?: {
    subscriptionId?: string;
    planId?: string;
    planName?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
    autoRenew?: boolean;
    paymentId?: string;
    txRef?: string;
    paymentLink?: string;
    amount?: number;
    currency?: string;
  };
}

export interface VerifyPlanSubscriptionResponse {
  success: boolean;
  message?: string;
  data?: Record<string, unknown>;
}

export interface PlanTopupRequest {
  planId: string;
  paymentMethod: 'wallet' | 'card' | 'crypto';
  walletType?: 'celo' | 'kxt' | 'fiat';
  balanceType?: 'celo' | 'kxt' | 'fiat';
  walletSource?: 'celo' | 'kxt' | 'fiat';
  crypto?: 'USDT' | 'USDC';
  chain?: 'SOLANA_DEVNET' | 'BSC_TESTNET';
  baseFiat?: string;
}

export interface PlanTopupResponse {
  success: boolean;
  message?: string;
  data?: {
    paymentLink?: string;
    txRef?: string;
    amount?: number;
    currency?: string;
  };
}

export interface VerifyPlanTopupResponse {
  success: boolean;
  message?: string;
  data?: Record<string, unknown>;
}

// ==================== API CALLS ====================

/**
 * Get all available plans
 */
export const getPlans = async (): Promise<PlansResponse> => {
  try {
    const response = await apiClient.get<PlansResponse>(ENDPOINTS.PLAN.GET_ALL);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching plans:', error);
    throw new Error(getErrorMessage(error) || 'Failed to fetch plans');
  }
};

/**
 * Subscribe to a plan
 * @param planId - The ID of the plan to subscribe to
 * @param payload - Subscription payload (payment method, hotspot id, auto-renew)
 */
export const subscribeToPlan = async (
  planId: string,
  payload?: SubscribeRequest
): Promise<SubscribeResponse> => {
  try {
    // Build payload based on what backend expects
    // If your backend doesn't need a body, we'll send minimal data
    const requestBody = payload;
    
    // Only add fields if they're needed
    // if (paymentMethodId) {
    //   payload.paymentMethodId = paymentMethodId;
    // }
    
    // // Add autoRenew if it's not the default
    // if (autoRenew !== true) {
    //   payload.autoRenew = autoRenew;
    // }

    // ✅ Using the correct endpoint: /plans/{id}/subscribe
    // The planId is in the URL, not the body
    const response = await apiClient.post<SubscribeResponse>(
      ENDPOINTS.PLAN.SUBSCRIBE(planId),
      requestBody && Object.keys(requestBody).length > 0 ? requestBody : undefined
    );
    
    return response.data;
  } catch (error: unknown) {
    console.error('Error subscribing to plan:', error);
    console.error('Error response:', (error as AxiosError).response?.data);
    
    // Handle specific error cases
    if ((error as AxiosError).response?.status === 404) {
      throw new Error('Plan not found or subscription endpoint unavailable');
    }
    
    if ((error as AxiosError).response?.status === 400) {
      const errorCode = getErrorCode(error);
      const errorMessage = getErrorMessage(error);

      switch (errorCode) {
        case 'NO_PAYMENT_METHOD':
          throw new Error(errorMessage || 'Please add a payment method before subscribing');
        case 'SUBSCRIPTION_ACTIVE':
          throw new Error(errorMessage || 'You already have an active subscription');
        case 'INSUFFICIENT_BALANCE':
        case 'INSUFFICIENT_FUNDS':
          throw new Error(errorMessage || 'Insufficient balance to complete subscription');
        case 'PLAN_NOT_AVAILABLE':
          throw new Error(errorMessage || 'This plan is no longer available');
        default:
          // Always prefer the backend's real message.
          throw new Error(errorMessage || 'Failed to subscribe to plan');
      }
    }
    
    // Throw the error message from backend if available
    const errorMessage = getErrorMessage(error) ||
                        (error as AxiosError).message ||
                        'Failed to subscribe to plan';
    throw new Error(errorMessage);
  }
};

export const verifyPlanSubscription = async (params: {
  status: string;
  tx_ref: string;
  transaction_id: string;
}): Promise<VerifyPlanSubscriptionResponse> => {
  try {
    const query = buildQueryString(params);
    const response = await apiClient.get<VerifyPlanSubscriptionResponse>(
      `${ENDPOINTS.PLAN.VERIFY}${query}`
    );
    return response.data;
  } catch (error: unknown) {
    console.error('Error verifying plan subscription:', error);
    const errorMessage = getErrorMessage(error) || (error as AxiosError).message || 'Failed to verify subscription';
    throw new Error(errorMessage);
  }
};

/**
 * Get plan details by ID
 * Note: This might need to filter from the all plans response
 * since the API doesn't have a specific endpoint for single plan
 */
export const getPlanById = async (planId: string): Promise<Plan | null> => {
  try {
    const response = await getPlans();
    const allPlans = [
      ...response.data.daily,
      ...response.data.weekly,
      ...response.data.monthly
    ];
    
    return allPlans.find(plan => plan.id === planId) || null;
  } catch (error: unknown) {
    console.error('Error fetching plan by ID:', error);
    throw new Error('Failed to fetch plan details');
  }
};

/**
 * Compare plans by period
 */
export const getPlansByPeriod = async (
  period: 'day' | 'week' | 'month'
): Promise<Plan[]> => {
  try {
    const response = await getPlans();
    return response.data[period === 'day' ? 'daily' : period === 'week' ? 'weekly' : 'monthly'];
  } catch (error: unknown) {
    console.error('Error fetching plans by period:', error);
    throw new Error('Failed to fetch plans');
  }
};

export const initiatePlanTopup = async (
  payload: PlanTopupRequest
): Promise<PlanTopupResponse> => {
  try {
    const response = await apiClient.post<PlanTopupResponse>(
      ENDPOINTS.PLAN.TOPUP_INIT,
      payload
    );
    return response.data;
  } catch (error: unknown) {
    console.error('Error initiating plan topup:', error);
    const errorMessage = getErrorMessage(error) || (error as AxiosError).message || 'Failed to initiate top up';
    throw new Error(errorMessage);
  }
};

export const verifyPlanTopup = async (params: {
  status: string;
  tx_ref: string;
  transaction_id: string;
}): Promise<VerifyPlanTopupResponse> => {
  try {
    const query = buildQueryString(params);
    const response = await apiClient.get<VerifyPlanTopupResponse>(
      `${ENDPOINTS.PLAN.TOPUP_VERIFY}${query}`
    );
    return response.data;
  } catch (error: unknown) {
    console.error('Error verifying plan topup:', error);
    const errorMessage = getErrorMessage(error) || (error as AxiosError).message || 'Failed to verify top up';
    throw new Error(errorMessage);
  }
};

export default {
  getPlans,
  subscribeToPlan,
  verifyPlanSubscription,
  initiatePlanTopup,
  verifyPlanTopup,
  getPlanById,
  getPlansByPeriod,
};
